import React from "react";
import { connect } from "react-redux";
import ExternalLink from "components/ExternalLink";
import GDPROptIn from "./GDPROptIn";
import requiresOptIn from "utils/requiresOptIn";
import {
  Button,
  Form,
  Footer,
  CountrySelect,
  CanadaProvince,
  DEFAULT_PROVINCE,
} from "auth/components";
import { Error } from "account/styles";
import { SignInLink, ssaURL, privacyUrl, LegalAgreement } from "auth/Links";
import { checkEmail } from "auth/modules/sign-up";
import {
  validateGdpr,
  validateTerms,
  validateName,
  validateOrganization,
  validateZipCode,
  validatePassword,
  validateEmail,
} from "account/helpers/user";
import {
  TextInput,
  InputLabel,
  TextInputLabel,
  Checkbox,
} from "components/Form";
import { SUBSCRIPTION_MODE } from "organization/Billing/constants";

const AcceptTerms = props => {
  return (
    <TextInputLabel>
      <Checkbox
        name="user[accept_terms]"
        onChange={props.onTermsAccepted}
        checked={props.termsAccepted}
        value={props.termsAccepted}
        className={props.className}
      />
      {"I accept the "}
      <ExternalLink href={ssaURL}>Software Services Agreement</ExternalLink>
      {" & "}
      <ExternalLink href={privacyUrl}>Privacy Notice</ExternalLink>
    </TextInputLabel>
  );
};

const US_CODE = "US";
const CANADA_CODE = "CA";
class SignUp extends React.Component {
  constructor(props) {
    super(props);
    const { Auth = {} } = window;
    const {
      user_params = {},
      org_params = {},
      resource_errors = {},
      initial,
    } = Auth;
    const { name, email } = user_params;
    const errors = initial
      ? {}
      : {
          name: !user_params.name,
          email: !user_params.email,
          password: true,
          country: !user_params.country,
          zipCode: !user_params.zipCode,
          province: !user_params.province,
        };
    for (let field in resource_errors) {
      if (resource_errors[field].length) {
        errors[field] = resource_errors[field];
      }
    }
    this.state = {
      formWasSubmited: Object.entries(user_params).length > 0,
      // retained values from failed signup
      name: name || "",
      email: email || "",
      autoFocusEmail: !(email && !errors.email),
      orgName: org_params.name || "",
      phone: org_params.phone || "",
      country: user_params.country || US_CODE,
      zipCode: user_params.zipCode || "",
      province: user_params.province || DEFAULT_PROVINCE,
      gdprOptIn: false,
      termsAccepted: false,
      isPasswordValid: false,
      formSubmitting: false,
      errors,
    };
  }

  componentDidMount() {
    const script = document.createElement("script");
    script.src = "https://static.solarwinds.com/referrer-cookie.js";
    script.async = true;
    document.body.appendChild(script);
  }

  setValidationResult(validationResult, fieldName, fieldValue) {
    let state = {};
    state[fieldName] = fieldValue;
    state.errors = { ...this.state.errors };

    if (validationResult.isValid) {
      state.errors[fieldName] = "";
    } else {
      state.errors[fieldName] = validationResult.error;
    }
    this.setState(state);
  }

  onChangeEmail = e => {
    const email = e.target.value;

    if (!this.state.formWasSubmited) {
      this.setState({ email: email || "" });
      return;
    }
    const validationEmailResult = validateEmail(email);
    this.setValidationResult(validationEmailResult, "email", email);
  };

  onChangeUserName = e => {
    const userName = e.target.value;

    if (!this.state.formWasSubmited) {
      this.setState({ name: userName || "" });
      return;
    }
    const userValidationResult = validateName(userName);
    this.setValidationResult(userValidationResult, "name", userName);
  };

  onChangePassword = e => {
    const password = e.target.value;
    const validationResult = validatePassword(password || "");

    let state = {};
    state["isPasswordValid"] = validationResult.isValid;
    state.errors = { ...this.state.errors };
    if (validationResult.isValid) {
      state.errors["password"] = "";
    } else {
      state.errors["password"] = validationResult.error;
    }
    this.setState(state);
  };

  onOrganizationChange = e => {
    const organizationName = e.target.value;
    if (!this.state.formWasSubmited) {
      this.setState({ orgName: organizationName || "" });
      return;
    }

    const validationResult = validateOrganization(organizationName);
    this.setValidationResult(validationResult, "orgName", organizationName);
  };

  onZipCodeChange = e => {
    const zipCode = e.target.value;
    if (!this.state.formWasSubmited) {
      this.setState({ zipCode: zipCode || "" });
      return;
    }

    const validationResult = validateZipCode(zipCode);
    this.setValidationResult(validationResult, "zipCode", zipCode);
  };

  onChangeTerms = e => {
    const isTermAccepted = e.target.checked;

    if (!this.state.formWasSubmited) {
      this.setState({ termsAccepted: isTermAccepted });
      return;
    }

    const validationResult = validateTerms(isTermAccepted);
    this.setValidationResult(validationResult, "termsAccepted", isTermAccepted);
  };

  onChangeGdpr = e => {
    const gdprAccepted = e.target.checked;

    if (!this.state.formWasSubmited) {
      this.setState({ gdprOptIn: gdprAccepted });
      return;
    }
    const validationResult = validateGdpr(gdprAccepted);
    this.setValidationResult(validationResult, "gdprOptIn", gdprAccepted);
  };

  isFormValid() {
    const validationEmailResult = validateEmail(this.state.email);
    if (!validationEmailResult.isValid) {
      return false;
    }

    const validationUserResult = validateName(this.state.name);
    if (!validationUserResult.isValid) {
      return false;
    }

    if (this.state.country === US_CODE) {
      const validationZipCodeResult = validateZipCode(this.state.zipCode);
      if (!validationZipCodeResult.isValid) {
        return false;
      }
    }

    if (!this.state.isPasswordValid) {
      return false;
    }

    const validationOrganizationResult = validateOrganization(
      this.state.orgName
    );
    if (!validationOrganizationResult.isValid) {
      return false;
    }

    const validationTermsResult = validateTerms(this.state.termsAccepted);
    if (!validationTermsResult.isValid) {
      return false;
    }

    if (requiresOptIn(this.state.country)) {
      const validationGdprResult = validateGdpr(this.state.gdprOptIn);
      if (!validationGdprResult.isValid) {
        return false;
      }
    }

    return true;
  }

  signUp = e => {
    var errors = {};

    errors["password"] = this.state.errors.password;

    const validationEmailResult = validateEmail(this.state.email);
    if (!validationEmailResult.isValid) {
      errors["email"] = validationEmailResult.error;
    }

    const validationNameResult = validateName(this.state.name);
    if (!validationNameResult.isValid) {
      errors["name"] = validationNameResult.error;
    }

    const validationOrganizationResult = validateOrganization(
      this.state.orgName
    );
    if (!validationOrganizationResult.isValid) {
      errors["orgName"] = validationOrganizationResult.error;
    }

    if (this.state.country === US_CODE) {
      const validationzipCodeResult = validateZipCode(this.state.zipCode);
      if (!validationzipCodeResult.isValid) {
        errors["zipCode"] = validationzipCodeResult.error;
      }
    }

    const validationTermsResult = validateTerms(this.state.termsAccepted);
    if (!validationTermsResult.isValid) {
      errors["termsAccepted"] = validationTermsResult.error;
    }

    if (requiresOptIn(this.state.country)) {
      const validationGdprResult = validateGdpr(this.state.gdprOptIn);
      if (!validationGdprResult.isValid) {
        errors["gdprOptIn"] = validationGdprResult.error;
      }
    }

    this.setState({
      formWasSubmited: true,
      errors,
    });
  };

  onChangeFor(prop) {
    return e => {
      const value =
        e.target.type == "checkbox" ? e.target.checked : e.target.value;
      this.setState({ [prop]: value || "" });
    };
  }

  onAutoFocusEmail = e => {
    this.setState({ autoFocusEmail: null });
    e.target.select();
  };
  onBlurForEmail = e => {
    const email = e.target.value;
    if (email) {
      this.props.checkEmail(email);
    }
  };
  onSubmit = e => {
    this.setState({ formSubmitting: true });
  };

  render() {
    const {
      name,
      email,
      autoFocusEmail,
      orgName,
      phone,
      country,
      zipCode,
      province,
      termsAccepted,
      formSubmitting,
      isPasswordValid,
      formWasSubmited,
      errors,
    } = this.state;

    const isValid = this.isFormValid();
    const edition = this.props.edition;
    return (
      <Form
        action={edition ? `/users/${edition}` : "/users"}
        invalid={!isValid}
        title={`${!edition ? "Get Started With AppOptics" : ""}`}
        text={`${!edition
          ? "Full featured and free for 30 days. No credit card required."
          : ""}`}
        onSubmit={this.onSubmit}
        edition={edition}
      >
        <input type="hidden" name="_initial" value={false} />
        <InputLabel>
          Valid Business Email *
          <TextInput
            name="user[email]"
            placeholder="example@email.com"
            onChange={this.onChangeEmail}
            value={email || ""}
            autoFocus={autoFocusEmail}
            onFocus={autoFocusEmail ? this.onAutoFocusEmail : null}
            onBlur={this.onBlurForEmail}
            className={errors.email ? "error" : null}
          />
          {errors.email && <Error>{errors.email}</Error>}
        </InputLabel>
        <InputLabel>
          Name *
          <TextInput
            name="user[name]"
            placeholder="Your full name"
            onChange={this.onChangeUserName}
            value={name || ""}
            autoFocus={!!email && !name}
            className={errors.name ? "error" : null}
          />
          {errors.name && <Error>{errors.name}</Error>}
        </InputLabel>
        <InputLabel>
          Password *
          <TextInput
            name="user[password]"
            onChange={this.onChangePassword}
            autoFocus={!!name && !autoFocusEmail}
            type="password"
            ref={node => (this.passswordValue = node)}
            className={formWasSubmited && !isPasswordValid ? "error" : null}
          />
          {formWasSubmited &&
            !isPasswordValid &&
            errors.password && <Error>{errors.password}</Error>}
        </InputLabel>
        <InputLabel>
          Organization
          <TextInput
            name="organization[name]"
            placeholder="Your organization"
            onChange={this.onOrganizationChange}
            value={orgName || ""}
            className={errors.orgName ? "error" : null}
          />
          {errors.orgName && <Error>{errors.orgName}</Error>}
        </InputLabel>
        <InputLabel>
          Phone
          <TextInput
            name="organization[phone]"
            placeholder="(212) 555-5555"
            onChange={this.onChangeFor("phone")}
            value={phone || ""}
            type="tel"
          />
        </InputLabel>
        {edition === SUBSCRIPTION_MODE.DEVELOPER.toLowerCase() && (
          <input
            type="hidden"
            name="organization[account_type]"
            value={edition}
          />
        )}
        <CountrySelect
          onChangeFor={this.onChangeFor("country")}
          country={this.state.country}
        />

        {country === US_CODE && (
          <InputLabel>
            Zip Code *
            <TextInput
              name="user[zip_code]"
              placeholder="Your zipcode"
              onChange={this.onZipCodeChange}
              value={zipCode || ""}
              className={errors.zipCode ? "error" : null}
            />
            {errors.zipCode && <Error>{errors.zipCode}</Error>}
          </InputLabel>
        )}
        {country === CANADA_CODE && (
          <CanadaProvince
            onChangeFor={this.onChangeFor("province")}
            province={province}
          />
        )}

        {requiresOptIn(country) && (
          <React.Fragment>
            <GDPROptIn
              country={this.state.country}
              gdprOptIn={this.state.gdprOptIn}
              onOptIn={this.onChangeGdpr}
              className={errors.gdprOptIn ? "error" : null}
            />
            {errors.gdprOptIn && <Error>{errors.gdprOptIn}</Error>}
          </React.Fragment>
        )}
        <AcceptTerms
          termsAccepted={termsAccepted}
          onTermsAccepted={this.onChangeTerms}
          className={errors.termsAccepted ? "error" : null}
        />
        {errors.termsAccepted && <Error>{errors.termsAccepted}</Error>}
        <Button type="submit" onClick={this.signUp} disabled={formSubmitting}>
          Sign Me Up
        </Button>
        <Footer>
          {!requiresOptIn(country) && <LegalAgreement />}
          <SignInLink />
        </Footer>
      </Form>
    );
  }
}
function mapDispatchToProps(dispatch, props) {
  return {
    checkEmail: email => dispatch(checkEmail(email)),
  };
}
export default connect(undefined, mapDispatchToProps)(SignUp);
